import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Input,
  Tooltip,
  Pagination,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import { useTable } from '@refinedev/antd';
import { type BaseRecord, useInvalidate } from '@refinedev/core';
import { SmartTableColumn, SmartDataTableProps } from '../types';
import { buildActionsColumn } from '../ActionButtons';
import { FiltersComponent } from '../FiltersComponent';
import { CreateComponent } from '../CreateComponent';
import { ExportComponent } from '../ExportComponent';
import { ColumnVisibilityComponent } from '../ColumnVisibilityComponent';
import { MobileCardComponent } from '../MobileCardComponent';
import { MobileHeaderComponent } from '../MobileHeaderComponent';
import { MobilePaginationComponent } from '../MobilePaginationComponent';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useSidebar } from '@src/components/Layout/SidebarContext';
import { createStyles } from 'antd-style';
import { useTableState } from '../hooks/useTableState';
import {
  buildPatientSearchFilters,
  getPatientSearchParamValues,
} from '@src/utils/patientSearchFilters';

const { Title } = Typography;

// Utility function to format values based on format type
const formatValue = (
  value: any,
  format?: string,
  decimalPlaces: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return String(value);
  }

  switch (format) {
    case 'currency':
      return `₹${numValue.toFixed(decimalPlaces)}`;
    case 'percentage':
      return `${numValue.toFixed(decimalPlaces)}%`;
    case 'decimal':
    case 'number':
      return numValue.toFixed(decimalPlaces);
    default:
      return String(value);
  }
};

const useStyle = createStyles(({ css, token }) => {
  const { antCls } = token as any;
  return {
    customTable: css`
      ${antCls}-table {
        ${antCls}-table-container {
          ${antCls}-table-body,
          ${antCls}-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }

        // Ensure proper vertical scrolling with max height
        ${antCls}-table-body {
          overflow-y: auto !important;
          max-height: calc(100vh - 400px) !important;
        }

        // Fix table header to stay at top
        ${antCls}-table-thead {
          position: sticky !important;
          top: 0 !important;
          z-index: 1 !important;
          background: white !important;
        }

        // Ensure pagination is always visible and properly positioned
        ${antCls}-pagination {
          margin-top: 16px !important;
          margin-bottom: 0 !important;
          text-align: center !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          position: relative !important;
          z-index: 2 !important;
        }

        // Ensure table wrapper doesn't hide pagination
        ${antCls}-table-wrapper {
          overflow: visible !important;
        }

        // Responsive table styles
        @media (max-width: 768px) {
          ${antCls}-table-thead > tr > th,
          ${antCls}-table-tbody > tr > td {
            padding: 8px 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          // Force minimum column widths on small devices
          ${antCls}-table-thead > tr > th {
            min-width: 80px !important;
          }

          // Allow horizontal scrolling
          ${antCls}-table-container {
            overflow-x: auto !important;
          }
        }

        @media (max-width: 480px) {
          ${antCls}-table-thead > tr > th,
          ${antCls}-table-tbody > tr > td {
            padding: 6px 2px !important;
            font-size: 11px !important;
          }

          ${antCls}-table-thead > tr > th {
            min-width: 70px !important;
          }
        }
      }
    `,
    borderedIconButton: css`
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 4px 8px;
      background: #fff;
      transition: all 0.2s ease;
      min-width: 32px;
      height: 32px;

      &:hover {
        border-color: #40a9ff;
        color: #40a9ff;
        background: #f0f8ff;
      }

      &:active {
        border-color: #096dd9;
        color: #096dd9;
      }
    `,
  };
});

interface TableComponentProps {
  api: SmartDataTableProps['api'];
  title?: string | React.ReactNode;
  columns: SmartTableColumn[];
  search?: SmartDataTableProps['search'];
  showCreate?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  isFilter?: boolean;
  actionWidth?: number;
  customActions?: SmartDataTableProps['customActions'];
  pageSize?: number;
  pageOptions?: number[];
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showExport?: boolean;
  requireFiltersForExport?: boolean;
  showColumnVisibility?: boolean;
  exportFormats?: Array<{
    key: string;
    label: string;
    format: 'csv' | 'excel' | 'pdf' | 'json';
    icon?: React.ReactNode;
  }>;
  scroll?: any;
  rowKey?: string;
  createForm?: SmartDataTableProps['createForm'];
  createConfigs?: SmartDataTableProps['createConfigs'];
  dynamicCreateForm?: SmartDataTableProps['dynamicCreateForm'];
  formDisplayMode?: SmartDataTableProps['formDisplayMode'];
  drawerWidth?: number | string;
  modalWidth?: number | string;
  onCreateSuccess?: SmartDataTableProps['onCreateSuccess'];
  onCreateResourceChange?: (resource: string) => void;
  style?: React.CSSProperties;
  className?: string;
  cardProps?: any;
  tableProps?: any;
  onEditClick: (record: BaseRecord) => void;
  onDeleteClick: (record: BaseRecord) => void;
  onViewClick: (record: BaseRecord) => void;
  onRowClick?: (record: BaseRecord) => void | null;
  // View switching configuration
  viewTabs?: React.ReactNode;
  onViewChange?: (view: 'list' | 'calendar') => void;
  // Auto-open create configuration
  autoOpenCreate?: boolean;
  autoOpenCreateKey?: string;
  // Auto-open edit configuration
  autoOpenEditId?: string | number | null;
  // Create drawer close handler
  onCreateDrawerClose?: () => void;
  // Mobile card configuration
  mobileCardComponent?: React.ComponentType<any>;
}

export const TableComponent: React.FC<TableComponentProps> = ({
  api,
  title,
  columns,
  search = { enabled: false, fields: [] },
  showCreate = true,
  showEdit = true,
  showDelete = true,
  showView = false,
  isFilter = true,
  actionWidth,
  customActions = [],
  pageSize = 10,
  pageOptions,
  showSizeChanger = true,
  showQuickJumper = true,
  showExport = false,
  requireFiltersForExport = false,
  showColumnVisibility = true,
  exportFormats,
  scroll,
  rowKey = 'id',
  createForm,
  createConfigs,
  dynamicCreateForm,
  formDisplayMode = 'drawer',
  drawerWidth,
  modalWidth,
  onCreateSuccess,
  onCreateResourceChange,
  style,
  className,
  cardProps,
  tableProps,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onRowClick,
  // View switching configuration
  viewTabs,
  onViewChange,
  autoOpenCreate,
  autoOpenCreateKey,
  autoOpenEditId,
  onCreateDrawerClose,
  // Mobile card configuration
  mobileCardComponent: customMobileCardComponent,
}) => {
  const { styles } = useStyle();
  const invalidate = useInvalidate();
  const { isMobile } = useResponsiveLayout();
  const { toggleSidebar } = useSidebar();
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [forceTableRerender, setForceTableRerender] = useState(0);
  const [isDataRefetching, setIsDataRefetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileCreateOpen, setMobileCreateOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use persistent state management with localStorage
  const {
    visibleColumns,
    setVisibleColumns,
    filters,
    setFilters,
    sorters,
    setSorters,
    clearState,
  } = useTableState({
    resource: api.resource,
    columns,
  });

  // Local state for search (not persisted)
  const [searchValue, setSearchValue] = useState(search?.initialValue || '');
  const [searchInputValue, setSearchInputValue] = useState(search?.initialValue || '');
  const [activeFilters, setActiveFilters] = useState<any[]>([]);

  // Effect to handle initial search value
  useEffect(() => {
    if (search?.initialValue && search.initialValue.trim()) {
      setSearchValue(search.initialValue);
      setSearchInputValue(search.initialValue);
    }
  }, [search?.initialValue]);

  // Convert stored filters to activeFilters format when filters change
  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      const convertedFilters: any[] = [];
      
      Object.entries(filters).forEach(([field, value]) => {
        const column = columns.find(
          col => col.dataIndex === field || col.filterField === field
        );
        
        if (column?.filterType === 'daterange') {
          // For date ranges stored as arrays
          if (Array.isArray(value) && value.length === 2) {
            convertedFilters.push({
              field,
              operator: 'gte',
              value: value[0],
            });
            convertedFilters.push({
              field,
              operator: 'lte',
              value: value[1],
            });
          }
        } else if (Array.isArray(value)) {
          // For multiselect
          convertedFilters.push({
            field,
            operator: 'in',
            value,
          });
        } else {
          // For regular filters
          convertedFilters.push({
            field,
            operator: 'eq',
            value,
          });
        }
      });
      
      setActiveFilters(convertedFilters);
    } else {
      // Clear active filters if no stored filters
      setActiveFilters([]);
    }
  }, [filters, columns]); // Run when filters or columns change

  // Cleanup search timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Calculate dynamic scroll values
  const getDynamicScroll = () => {
    // Calculate total required width based on column minWidths
    let totalRequiredWidth = 0;

    columns.forEach(column => {
      if (column.minWidth) {
        totalRequiredWidth += column.minWidth;
      } else {
        totalRequiredWidth += 120; // Default column width
      }
    });

    // Add space for actions column if needed
    if (
      showCreate ||
      showEdit ||
      showDelete ||
      showView ||
      customActions.length > 0
    ) {
      totalRequiredWidth += actionWidth || 120;
    }

    // Add some padding
    totalRequiredWidth += 40;

    // Use the larger of calculated width or available width
    const horizontalScroll = Math.max(totalRequiredWidth, 800);

    // Calculate vertical scroll based on screen height
    const verticalScroll = Math.max(400, 600);

    return { x: horizontalScroll, y: verticalScroll };
  };

  // Get final scroll configuration - prioritize passed scroll props over calculated ones
  const getFinalScroll = () => {
    // If scroll is passed via tableProps, use it as base
    if (tableProps?.scroll) {
      const passedScroll = tableProps.scroll;
      const calculatedScroll = getDynamicScroll();

      return {
        x: passedScroll.x || calculatedScroll.x,
        y: passedScroll.y || calculatedScroll.y,
      };
    }

    // Otherwise use calculated scroll
    return getDynamicScroll();
  };

  // Table data using Refine's useTable
  const { tableProps: refineTableProps, setPageSize: setTablePageSize } =
    useTable({
      resource: api.resource,
      syncWithLocation: false,
      pagination: {
        current: currentPage,
        pageSize: pageSize,
        mode: 'server',
      },
      sorters: {
        initial:
          sorters.length > 0
            ? [
                {
                  field: sorters[0].field,
                  order: sorters[0].order,
                },
              ]
            : undefined,
        mode: 'server',
      },
      filters: {
        permanent: [
          // Default filters from api.defaultParams
          ...(api.defaultParams?.filters || []),
          // Search filters
          ...(searchValue && search.enabled
            ? api.resource === 'patients'
              ? buildPatientSearchFilters(searchValue, search.fields)
              : search.fields.map(field => ({
                  field,
                  operator: 'contains',
                  value: searchValue,
                }))
            : []),
          // Advanced filters
          ...activeFilters,
        ],
        mode: 'server',
      },
      meta: {
        ...api.defaultParams?.meta,
      },
    });

  // Debounced search
  const debouncedSearch = useCallback(
    (value: string) => {
      // Update input value immediately for UI responsiveness
      setSearchInputValue(value);

      // If search is cleared, close the search bar
      if (!value) {
        setIsSearchOpen(false);
      }

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout with configurable delay
      const debounceMs = search.debounceMs || 300; // Default to 300ms if not specified
      searchTimeoutRef.current = setTimeout(() => {
        setSearchValue(value);
        setCurrentPage(1); // Reset to first page on search
      }, debounceMs);
    },
    [search.debounceMs]
  );

  // Handle filter changes
  const handleFilterChange = useCallback((filters: any[]) => {
    setActiveFilters(filters);
    
    // Convert filters to storage format and save
    const filtersMap: Record<string, any> = {};
    
    // Group filters by field to handle date ranges (which have gte/lte pairs)
    const fieldGroups: Record<string, any[]> = {};
    filters.forEach(filter => {
      if (!fieldGroups[filter.field]) {
        fieldGroups[filter.field] = [];
      }
      fieldGroups[filter.field].push(filter);
    });
    
    // Process each field group
    Object.entries(fieldGroups).forEach(([field, fieldFilters]) => {
      if (fieldFilters.length === 2) {
        // Check if this is a date range (gte + lte)
        const gteFilter = fieldFilters.find(f => f.operator === 'gte');
        const lteFilter = fieldFilters.find(f => f.operator === 'lte');
        
        if (gteFilter && lteFilter) {
          // Store as array for date ranges
          filtersMap[field] = [gteFilter.value, lteFilter.value];
        } else {
          // Store the last value if not a date range
          filtersMap[field] = fieldFilters[fieldFilters.length - 1].value;
        }
      } else if (fieldFilters.length === 1) {
        // Single filter
        filtersMap[field] = fieldFilters[0].value;
      }
    });
    
    setFilters(filtersMap);
    setCurrentPage(1); // Reset to first page on filter change
  }, [setFilters]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchInputValue('');
    setSearchValue('');
    setCurrentPage(1);
    setIsSearchOpen(false); // Close search bar when clearing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Clear table state and reset data
  const handleClearTableState = useCallback(() => {
    setIsDataRefetching(true);
    clearState(); // Use the clearState from useTableState hook
    setSearchValue('');
    setSearchInputValue('');
    setActiveFilters([]);
    setCurrentPage(1);
    setForceTableRerender(prev => prev + 1);

    invalidate({
      resource: api.resource,
      invalidates: ['list'],
    });

    setTimeout(() => {
      setIsDataRefetching(false);
    }, 100);
  }, [clearState, api.resource, invalidate]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setActiveFilters([]);
    setFilters({});
    setCurrentPage(1);
    setForceTableRerender(prev => prev + 1);

    invalidate({
      resource: api.resource,
      invalidates: ['list'],
    });
  }, [invalidate, api.resource]);

  // Refresh table data
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    invalidate({
      resource: api.resource,
      invalidates: ['list'],
    });

    // Clear the refresh loading state after a short delay
    setTimeout(() => {
      setIsRefreshing(false);
    }, 100);
  }, [api.resource, invalidate]);

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback(
    (newVisibleColumns: string[]) => {
      setVisibleColumns(newVisibleColumns);
    },
    []
  );

  // Handle table sorting change
  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    // Handle action based on type
    switch (extra.action) {
      case 'sort':
        // Handle sorting changes
        const newSorters = sorter.field
          ? [
              {
                field: sorter.field,
                order:
                  sorter.order === 'ascend'
                    ? ('asc' as const)
                    : ('desc' as const),
              },
            ]
          : [];
        setSorters(newSorters);
        setCurrentPage(1); // Reset to first page on sort
        // Call onChange with page 1
        if (refineTableProps.onChange) {
          refineTableProps.onChange(
            { ...pagination, current: 1 },
            filters,
            sorter,
            extra
          );
        }
        break;

      case 'paginate':
        // Handle pagination changes
        if (pagination?.current !== undefined) {
          setCurrentPage(pagination.current);
          // Update page size if it changed
          if (pagination.pageSize !== pageSize) {
            setTablePageSize(pagination.pageSize);
          }
          // Call onChange with the new page
          if (refineTableProps.onChange) {
            refineTableProps.onChange(pagination, filters, sorter, extra);
          }
        }
        break;

      default:
        // For other changes, call onChange normally
        if (refineTableProps.onChange) {
          refineTableProps.onChange(pagination, filters, sorter, extra);
        }
        break;
    }
  };

  // Build actions column using imported function
  const actionsColumn = buildActionsColumn({
    showView,
    showEdit,
    showDelete,
    actionWidth,
    customActions,
    onViewClick,
    onEditClick,
    onDeleteClick,
  });

  // Convert SmartTableColumn to Ant Design column format
  const convertToAntdColumns = (columns: SmartTableColumn[]) => {
    const filteredColumns = columns.filter(column =>
      (visibleColumns || []).includes(column.key)
    );
    return filteredColumns.map(column => {
      const antdColumn: any = { ...column };
      // Convert sorter property to Ant Design format
      if (column.sorter || column.sortable) {
        antdColumn.sorter = true;
        // Enable sorting directions
        antdColumn.sortDirections = ['ascend', 'descend'];

        // Set default sort order if this column is in sorters
        const storedSorter = sorters.find(
          (s: { field: string; order: 'asc' | 'desc' }) =>
            s.field === column.key
        );
        if (storedSorter) {
          antdColumn.defaultSortOrder =
            storedSorter.order === 'asc' ? 'ascend' : 'descend';
        }
      }

      // Initialize style object
      antdColumn.style = {
        ...antdColumn.style,
      };

      // Handle minWidth and maxWidth
      if (column.minWidth) {
        // Apply minWidth to both Ant Design property and style for better compatibility
        antdColumn.minWidth = column.minWidth;
        antdColumn.width = column.minWidth; // Also set width property
        antdColumn.style.minWidth = `${column.minWidth}px`;
        antdColumn.style.width = `${column.minWidth}px`;

        // Ensure column doesn't shrink below minWidth
        antdColumn.onHeaderCell = () => ({
          style: {
            minWidth: `${column.minWidth}px !important`,
            width: `${column.minWidth}px !important`,
          },
        });

        antdColumn.onCell = () => ({
          style: {
            minWidth: `${column.minWidth}px !important`,
            width: `${column.minWidth}px !important`,
          },
        });
      }

      if (column.maxWidth) {
        antdColumn.maxWidth = column.maxWidth;
        antdColumn.style.maxWidth = `${column.maxWidth}px`;
      }

      // Handle alignment with CSS text-align
      if (column.align) {
        antdColumn.align = column.align;
        antdColumn.style.textAlign = column.align;
      }

      // Handle formatting in render function
      if (column.format && !column.render) {
        const originalRender = antdColumn.render;
        antdColumn.render = (value: any, record: any) => {
          if (originalRender) {
            return originalRender(value, record);
          }
          const formattedValue = formatValue(
            value,
            column.format,
            column.decimalPlaces
          );
          return (
            <div style={{ textAlign: column.align || 'left' }}>
              {formattedValue}
            </div>
          );
        };
      }

      return antdColumn;
    });
  };

  // Final columns with actions
  const finalColumns = [...convertToAntdColumns(columns)];
  if (
    showCreate ||
    showEdit ||
    showDelete ||
    showView ||
    customActions.length > 0
  ) {
    finalColumns.push(actionsColumn);
  }

  // Ensure table props pagination matches our state
  const finalTableProps = {
    ...refineTableProps,
    pagination: {
      ...refineTableProps.pagination,
      current: currentPage,
      pageSize: pageSize,
      ...(refineTableProps.pagination && {
        total: refineTableProps.pagination.total,
      }),
    },
  };

  // Merge Card bodyStyle to remove extra bottom spacing below pagination
  const mergedCardProps = {
    ...cardProps,
    bodyStyle: {
      paddingBottom: 0,
      ...(cardProps?.bodyStyle || {}),
    },
  };


  // Mobile layout
  if (isMobile) {
    return (
      <div style={{ 
        background: '#f5f5f5', 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Mobile Header */}
        <MobileHeaderComponent
         title={
          typeof title === 'string'
            ? title
            : api.resource.charAt(0).toUpperCase() + api.resource.slice(1)
        }
          onMenuClick={toggleSidebar}
          onSearch={search.enabled ? debouncedSearch : undefined}
          onSearchToggle={() => setIsSearchOpen(!isSearchOpen)}
          isSearchOpen={isSearchOpen}
          onFilterClick={() => {
            // Trigger filter functionality for mobile
            if (isFilter) {
              setMobileFilterOpen(true);
            }
          }}
          onExportClick={() => {
            // Trigger export functionality similar to desktop
            if (showExport) {
              // Create a temporary export component instance to handle the export
              const exportHandler = async () => {
                try {
                  // Use the same logic as ExportComponent
                  const queryParams: Record<string, any> = { format: 'csv' };

                  // Add search filters
                  if (searchValue && search.enabled && search.fields.length > 0) {
                    if (api.resource === 'patients') {
                      const searchParams = getPatientSearchParamValues(
                        searchValue,
                        search.fields
                      );
                      Object.entries(searchParams).forEach(([field, val]) => {
                        queryParams[`search[${field}]`] = val;
                      });
                    } else {
                      search.fields.forEach(field => {
                        queryParams[`search[${field}]`] = searchValue;
                      });
                    }
                  }

                  // Add advanced filters
                  if (activeFilters.length > 0) {
                    activeFilters.forEach(filter => {
                      if (filter.operator === 'contains') {
                        queryParams[`filter[${filter.field}_like]`] = filter.value;
                      } else if (filter.operator === 'eq') {
                        queryParams[`filter[${filter.field}]`] = filter.value;
                      } else if (filter.operator === 'gte') {
                        queryParams[`filter[${filter.field}_gte]`] = filter.value;
                      } else if (filter.operator === 'lte') {
                        queryParams[`filter[${filter.field}_lte]`] = filter.value;
                      } else if (filter.operator === 'in') {
                        queryParams[`filter[${filter.field}_in]`] = Array.isArray(filter.value)
                          ? filter.value.join(',')
                          : filter.value;
                      } else {
                        queryParams[`filter[${filter.field}]`] = filter.value;
                      }
                    });
                  }

                  // Call export API
                  const { apiClient } = await import('@src/utils/axios');
                  const response = await apiClient.get(`/${api.resource}/export`, {
                    params: queryParams,
                    responseType: 'blob',
                  });

                  if (response.data) {
                    // Trigger file download
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${api.resource}_export_${new Date().toISOString().split('T')[0]}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    
                    message.success('Export completed successfully!');
                  }
                } catch (error: any) {
                  console.error('Export failed:', error);
                  message.error(error?.message || 'Export failed. Please try again.');
                }
              };
              exportHandler();
            }
          }}
          onRefreshClick={handleRefresh}
          onCreateClick={() => {
            // Trigger create functionality for mobile
            if (showCreate && createForm) {
              setMobileCreateOpen(true);
            }
          }}
          searchPlaceholder={search.placeholder || `Search ${api.resource}...`}
          showSearch={search.enabled}
          showFilter={isFilter}
          showExport={showExport}
          showRefresh={true}
          showCreate={showCreate}
        />

        {/* Mobile Cards - Scrollable Area */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: '25px', // Space for pagination at bottom
          WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
        }}>
          <Spin spinning={!!refineTableProps.loading}>
            {customMobileCardComponent && React.createElement(customMobileCardComponent, {
              columns: columns.filter(col => visibleColumns.includes(col.key)),
              dataSource: Array.isArray(finalTableProps?.dataSource)
                ? finalTableProps.dataSource
                : [],
              onEditClick,
              onDeleteClick,
              onViewClick,
              onRowClick,
              showEdit,
              showDelete,
              showView,
              customActions,
              rowKey,
              title: typeof title === 'string' ? title : api.resource
            })}
            {!customMobileCardComponent && (
              <div style={{ 
                padding: '40px 20px', 
                textAlign: 'center', 
                color: '#999',
                fontSize: '14px'
              }}>
                No mobile card component provided
              </div>
            )}
          </Spin>
        </div>

              {/* Mobile Pagination - Fixed at bottom */}
              {finalTableProps.pagination && (
                <div style={{ 
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 100
                }}>
            <MobilePaginationComponent
              current={finalTableProps.pagination?.current || 1}
              total={finalTableProps.pagination?.total || 0}
              pageSize={finalTableProps.pagination?.pageSize || pageSize}
              onChange={(page, pageSize) => {
                handleTableChange(
                  { current: page, pageSize },
                  {},
                  {},
                  { action: 'paginate' }
                );
              }}
              onShowSizeChange={(current, size) => {
                handleTableChange(
                  { current, pageSize: size },
                  {},
                  {},
                  { action: 'paginate' }
                );
              }}
              showSizeChanger={showSizeChanger}
              pageSizeOptions={pageOptions?.map(opt => String(opt))}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
            />
          </div>
        )}

              {/* Mobile Create Form */}
              {showCreate && createForm && (
                <CreateComponent
                  api={api}
                  createForm={createForm}
                  formDisplayMode={formDisplayMode}
                  drawerWidth={drawerWidth}
                  modalWidth={modalWidth}
                  showCreate={true} // Keep true so component renders
                  autoOpen={mobileCreateOpen}
                  onSuccess={data => {
                    // Invalidate table data to refresh the list
                    invalidate({
                      resource: api.resource,
                      invalidates: ['list'],
                    });
                    // Call the original onSuccess callback if provided
                    onCreateSuccess?.(data);
                    // Close the mobile create form
                    setMobileCreateOpen(false);
                  }}
                  onDrawerClose={() => {
                    setMobileCreateOpen(false);
                  }}
                  onResourceChange={onCreateResourceChange}
                  customButton={{
                    label: '',
                    style: { display: 'none' } // Hide the button visually
                  }}
                />
              )}

        {/* Mobile Filter Component */}
        {isFilter && (
          <FiltersComponent
            columns={columns}
            onApplyFilters={handleFilterChange}
            onClearFilters={handleClearFilters}
            activeFiltersCount={activeFilters.length}
            autoOpen={mobileFilterOpen}
            onDrawerClose={() => {
              setMobileFilterOpen(false);
            }}
            initialFilters={filters}
          />
        )}
      </div>
    );
  }

  // Desktop layout
  return (
    <>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                {title ||
                  api.resource.charAt(0).toUpperCase() + api.resource.slice(1)}
              </Title>
            </Col>
            <Col>
              <Space>
                {viewTabs && viewTabs}
                {search.enabled && (
                  <Input
                    placeholder={
                      search.placeholder || `Search ${api.resource}...`
                    }
                    value={searchInputValue}
                    onChange={e => debouncedSearch(e.target.value)}
                    style={{ width: '250px' }}
                    allowClear
                    onClear={handleClearSearch}
                    prefix={<SearchOutlined />}
                    suffix={
                      searchInputValue !== searchValue &&
                      searchInputValue !== '' ? (
                        <Spin size="small" />
                      ) : null
                    }
                  />
                )}
                {isFilter && (
                  <FiltersComponent
                    columns={columns}
                    onApplyFilters={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    activeFiltersCount={activeFilters.length}
                    initialFilters={filters}
                  />
                )}
                {createConfigs && createConfigs.length > 0 ? (
                  // Multiple create buttons
                  createConfigs.map(config => (
                    <CreateComponent
                      key={config.key}
                      api={{
                        ...api,
                        resource: config.resource || api.resource,
                      }}
                      createForm={config.form}
                      formDisplayMode={formDisplayMode}
                      drawerWidth={drawerWidth}
                      modalWidth={modalWidth}
                      showCreate={showCreate}
                      onSuccess={data => {
                        // Invalidate table data to refresh the list
                        invalidate({
                          resource: config.resource || api.resource,
                          invalidates: ['list'],
                        });
                        // Call the original onSuccess callback if provided
                        onCreateSuccess?.(data);
                      }}
                      autoOpen={
                        autoOpenCreate &&
                        (!autoOpenCreateKey || config.key === autoOpenCreateKey)
                      }
                      onDrawerClose={onCreateDrawerClose}
                      customButton={{
                        label: config.label,
                        icon: config.icon,
                        style: config.style,
                        type: config.type,
                      }}
                    />
                  ))
                ) : dynamicCreateForm ? (
                  // Dynamic create form
                  <CreateComponent
                    api={api}
                    createForm={dynamicCreateForm.getForm(api.resource)}
                    formDisplayMode={formDisplayMode}
                    drawerWidth={drawerWidth}
                    modalWidth={modalWidth}
                    showCreate={showCreate}
                    onSuccess={data => {
                      // Invalidate table data to refresh the list
                      invalidate({
                        resource: api.resource,
                        invalidates: ['list'],
                      });
                      // Call the original onSuccess callback if provided
                      onCreateSuccess?.(data);
                    }}
                    autoOpen={autoOpenCreate}
                    onDrawerClose={onCreateDrawerClose}
                    onResourceChange={onCreateResourceChange}
                  />
                ) : (
                  // Single create form
                  <CreateComponent
                    api={api}
                    createForm={createForm}
                    formDisplayMode={formDisplayMode}
                    drawerWidth={drawerWidth}
                    modalWidth={modalWidth}
                    showCreate={showCreate}
                    onSuccess={data => {
                      // Invalidate table data to refresh the list
                      invalidate({
                        resource: api.resource,
                        invalidates: ['list'],
                      });
                      // Call the original onSuccess callback if provided
                      onCreateSuccess?.(data);
                    }}
                    autoOpen={autoOpenCreate}
                    onDrawerClose={onCreateDrawerClose}
                  />
                )}
                <ColumnVisibilityComponent
                  columns={columns}
                  visibleColumns={visibleColumns}
                  onColumnVisibilityChange={handleColumnVisibilityChange}
                  onResetToDefault={() =>
                    setVisibleColumns(columns.map(col => col.key))
                  }
                  showColumnVisibility={showColumnVisibility}
                />
                <ExportComponent
                  api={api}
                  searchValue={searchValue}
                  searchFields={search.enabled ? search.fields : []}
                  activeFilters={activeFilters}
                  exportFormats={exportFormats}
                  showExport={showExport}
                  requireFiltersForExport={requireFiltersForExport}
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={isRefreshing || refineTableProps.loading}
                />

                <Tooltip
                  title="Reset column visibility, filters, sorting, search, and refresh data"
                  placement="bottom"
                >
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearTableState}
                    size="small"
                    className={styles.borderedIconButton}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>
        }
        {...mergedCardProps}
        style={style}
        className={className}
      >
        <Spin spinning={!!refineTableProps.loading}>
          {(() => {
            const finalScroll = getFinalScroll();
            return (
              <Table
                key={`table-${forceTableRerender}`}
                {...finalTableProps}
                dataSource={
                  Array.isArray(finalTableProps?.dataSource)
                    ? finalTableProps.dataSource
                    : []
                }
                className={styles.customTable}
                columns={finalColumns}
                scroll={finalScroll}
                size="small"
                rowKey={rowKey}
                tableLayout="fixed"
                onChange={handleTableChange}
                onRow={
                  onRowClick
                    ? record => ({
                        onClick: e => {
                          // Don't trigger row click if clicking on action buttons
                          const target = e.target as HTMLElement;
                          if (
                            target.closest('.ant-btn') ||
                            target.closest('.ant-dropdown-trigger')
                          ) {
                            return;
                          }
                          onRowClick(record);
                        },
                        style: { cursor: 'pointer' },
                      })
                    : undefined
                }
                pagination={{
                  ...refineTableProps.pagination,
                  showSizeChanger,
                  showQuickJumper,
                  pageSizeOptions: pageOptions,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  position: ['bottomCenter'],
                  // Ensure pagination is always visible
                  hideOnSinglePage: false,
                }}
                {...tableProps}
              />
            );
          })()}
        </Spin>
      </Card>
    </>
  );
};
